import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clientAPI } from '../../../services/apiServices';
import { Alert, Input, Modal } from '../../../components/common/UI';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { formatDocumentInput, formatEmailInput, formatPhoneInput } from '../../../utils/inputFormat';
import { requiredFieldMessage } from '../../../utils/validationMessages';

const EMPTY = { name: '', document: '', phone: '', email: '' };

const QuickContactModal = ({ isOpen, onClose, onCreated, clientType = 'involved' }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const close = () => {
    setForm(EMPTY);
    setFieldErrors({});
    setError('');
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      const message = requiredFieldMessage(t, t('quickContact.nameLabel'));
      setFieldErrors({ name: message });
      setError(message);
      return;
    }
    if (form.name.trim().length < 2) {
      setFieldErrors({ name: t('quickContact.nameRequired') });
      setError(t('quickContact.nameRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const { data } = await clientAPI.create({
        name: form.name.trim(),
        document: form.document.trim() || null,
        phone: form.phone.trim() || null,
        whatsapp: form.phone.trim() || null,
        email: form.email.trim() || null,
        clientType,
      });
      const contact = data.data || data;
      setForm(EMPTY);
      await onCreated(contact);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, t('quickContact.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={t('quickContact.title')} size="sm">
      {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}
      <div className="mt-4 space-y-4">
        <Input
          label={t('quickContact.nameLabel')}
          value={form.name}
          onChange={(event) => { setForm({ ...form, name: event.target.value }); setFieldErrors((current) => ({ ...current, name: '' })); }}
          placeholder={t('quickContact.namePlaceholder')}
          error={fieldErrors.name}
        />
        <Input
          label={t('clientForm.documentLabel')}
          value={form.document}
          onChange={(event) => setForm({ ...form, document: formatDocumentInput(event.target.value) })}
          placeholder={t('clientForm.documentPlaceholder')}
          helpText={t('clientForm.documentHelp')}
        />
        <Input
          label={t('quickContact.phoneLabel')}
          value={form.phone}
          onChange={(event) => setForm({ ...form, phone: formatPhoneInput(event.target.value) })}
          placeholder={t('auth.phonePlaceholder')}
        />
        <Input
          label={t('quickContact.emailLabel')}
          type="email"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: formatEmailInput(event.target.value) })}
          placeholder={t('auth.emailPlaceholder')}
        />
        <div className="flex gap-2">
          <button className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white disabled:opacity-60" onClick={handleSave} disabled={saving} type="button">
            {saving ? t('common.saving') : t('common.save')}
          </button>
          <button className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900" onClick={close} type="button">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuickContactModal;
